/* ──────────────────────────────────────────────────────────────────────────
 * Ticker config — the universe of instruments the simulator can model.
 *
 * This file is the single source of truth for **which tickers exist**. The
 * refresh script (`scripts/refresh-prices.mjs`) reads from here, fetches each
 * ticker's full available history from yahoo-finance2, and writes one
 * `<slug>.json` per ticker into this same directory. The JSON files are the
 * committed cache; the simulator reads them at runtime via the data loader.
 *
 * **Adding a new ticker:**
 *   1. Add a one-line entry below (canonical symbol → meta).
 *   2. Run `npm run refresh-prices`.
 *   3. A new `<slug>.json` file appears in this directory.
 *   4. `git add` + commit.
 *
 * **Removing a ticker:**
 *   1. Delete the entry below.
 *   2. Run `npm run refresh-prices` — the orphan JSON file gets deleted.
 *   3. Commit the deletion.
 *
 * Canonical symbols use Yahoo's convention (`<SYMBOL>.TO` for TSX, bare
 * symbol for US tickers, hyphens for class shares like `BRK-B` and
 * `RCI-B.TO`). The refresh script queries Yahoo directly using the
 * canonical symbol, so no separate mapping is needed.
 *
 * **Eligibility rule for individual stocks** (Slice 9): publicly-traded
 * companies are included regardless of current dividend status. The purpose
 * test under ITA s. 20(1)(c) requires "reasonable expectation of producing
 * income," and a publicly-traded corporation can declare dividends at any
 * time. ETFs and other instruments are subject to a stricter "currently
 * pays dividends" filter (all 20 ETFs below meet it).
 *
 * **Interlisted companies** (e.g., Royal Bank trades as both RY.TO and RY
 * on NYSE) are listed under their Canadian listing as the canonical entry.
 * If a user searches for "RY", they find RY.TO. The US listing isn't
 * separately included.
 *
 * **Flavor text convention:** ETFs carry rich SM-relevance notes
 * (`note` field). Individual stocks just carry the company name; no note.
 *
 * The 230+ tickers below cover the practical universe Canadian leveraged
 * investors actually hold or commonly consider: S&P 500 exposure (4 ETFs),
 * all-in-one global portfolios, dividend-focused ETFs, Canadian broad
 * market, the TSX 60 + selected Canadian mid-caps, the S&P 100 + selected
 * S&P 500 US names.
 * ────────────────────────────────────────────────────────────────────────── */

export type Currency = "CAD" | "USD";

/**
 * Category for grouping in the ticker dropdown. Order in the UI follows
 * the order of the entries in TICKER_CONFIG below — keep grouped entries
 * adjacent for natural-looking dropdowns.
 */
export type TickerCategory =
  | "S&P 500 ETFs"
  | "All-in-one portfolios"
  | "Canadian / world broad market"
  | "Canadian dividend ETFs"
  | "US-listed ETFs"
  | "Canadian stocks"
  | "US stocks";

export type TickerMeta = {
  /** Canonical symbol — also the Yahoo Finance query symbol. */
  ticker: string;
  /** Quoted currency. */
  currency: Currency;
  /** Short human-readable name. */
  name: string;
  /** Group in the ticker dropdown. */
  category: TickerCategory;
  /** One-line description, including any tax/structure note relevant to SM.
   *  Used on ETFs; individual-stock entries omit this. */
  note?: string;
};

export const TICKER_CONFIG: Record<string, TickerMeta> = {
  // ── S&P 500 ETFs (4 variants — the most-debated bucket in SM communities) ──
  "VFV.TO": {
    ticker: "VFV.TO",
    currency: "CAD",
    category: "S&P 500 ETFs",
    name: "Vanguard S&P 500 Index ETF",
    note: "CAD-traded, unhedged. The universal SM 'easy mode' pick.",
  },
  "HXS.TO": {
    ticker: "HXS.TO",
    currency: "CAD",
    category: "S&P 500 ETFs",
    name: "Global X S&P 500 Index Corporate Class ETF",
    note: "Swap-based; no foreign withholding tax. SM-tax-optimal. Pays no cash dividends — distributions are converted internally to capital gains.",
  },
  "XSP.TO": {
    ticker: "XSP.TO",
    currency: "CAD",
    category: "S&P 500 ETFs",
    name: "iShares Core S&P 500 Index ETF (CAD-Hedged)",
    note: "CAD-hedged. Removes USD/CAD currency risk.",
  },
  VOO: {
    ticker: "VOO",
    currency: "USD",
    category: "S&P 500 ETFs",
    name: "Vanguard S&P 500 ETF",
    note: "US-listed; for USD margin investors.",
  },

  // ── All-in-one global portfolios (one-fund solutions) ──
  "XEQT.TO": {
    ticker: "XEQT.TO",
    currency: "CAD",
    category: "All-in-one portfolios",
    name: "iShares Core Equity ETF Portfolio",
    note: "100% global equity, one-fund. Inception 2019.",
  },
  "VEQT.TO": {
    ticker: "VEQT.TO",
    currency: "CAD",
    category: "All-in-one portfolios",
    name: "Vanguard All-Equity ETF Portfolio",
    note: "Vanguard's all-equity one-fund. Inception 2019.",
  },

  // ── Canadian broad market + world ──
  "XIC.TO": {
    ticker: "XIC.TO",
    currency: "CAD",
    category: "Canadian / world broad market",
    name: "iShares Core S&P/TSX Capped Composite Index ETF",
    note: "Canadian broad-market exposure.",
  },
  "VUN.TO": {
    ticker: "VUN.TO",
    currency: "CAD",
    category: "Canadian / world broad market",
    name: "Vanguard US Total Market Index ETF",
    note: "CAD-traded, unhedged US total market.",
  },
  "XAW.TO": {
    ticker: "XAW.TO",
    currency: "CAD",
    category: "Canadian / world broad market",
    name: "iShares Core MSCI All Country World ex Canada Index ETF",
    note: "World ex-Canada. Pairs with XIC for global allocation.",
  },

  // ── Canadian dividend ETFs (popular for income-focused SM plays) ──
  "VDY.TO": {
    ticker: "VDY.TO",
    currency: "CAD",
    category: "Canadian dividend ETFs",
    name: "Vanguard FTSE Canadian High Dividend Yield Index ETF",
    note: "High-dividend Canadian equity. Common SM pick.",
  },
  "XEI.TO": {
    ticker: "XEI.TO",
    currency: "CAD",
    category: "Canadian dividend ETFs",
    name: "iShares S&P/TSX Composite High Dividend Index ETF",
    note: "iShares counterpart to VDY.",
  },
  "ZDV.TO": {
    ticker: "ZDV.TO",
    currency: "CAD",
    category: "Canadian dividend ETFs",
    name: "BMO Canadian Dividend ETF",
    note: "BMO's high-dividend Canadian fund.",
  },

  // ── US-listed ETFs (for USD margin investors) ──
  VTI: {
    ticker: "VTI",
    currency: "USD",
    category: "US-listed ETFs",
    name: "Vanguard Total Stock Market ETF",
    note: "Whole US market.",
  },
  QQQ: {
    ticker: "QQQ",
    currency: "USD",
    category: "US-listed ETFs",
    name: "Invesco QQQ Trust (Nasdaq 100)",
    note: "US growth / tech-heavy.",
  },
  SPY: {
    ticker: "SPY",
    currency: "USD",
    category: "US-listed ETFs",
    name: "SPDR S&P 500 ETF Trust",
    note: "Longest history (1993); shows dot-com bust + 2008 GFC.",
  },

  // ── Canadian stocks (alphabetical by ticker) ──

  "AEM.TO": {
    ticker: "AEM.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Agnico Eagle Mines",
  },
  "AQN.TO": {
    ticker: "AQN.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Algonquin Power & Utilities",
  },
  "ALA.TO": { ticker: "ALA.TO", currency: "CAD", category: "Canadian stocks", name: "AltaGas" },
  "AP-UN.TO": {
    ticker: "AP-UN.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Allied Properties REIT",
  },
  "ARX.TO": {
    ticker: "ARX.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "ARC Resources",
  },
  "ATD.TO": {
    ticker: "ATD.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Alimentation Couche-Tard",
  },
  "BAM.TO": {
    ticker: "BAM.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Brookfield Asset Management",
    note: "Reborn 2022 after split from BBU. Data starts late 2022.",
  },
  "BCE.TO": { ticker: "BCE.TO", currency: "CAD", category: "Canadian stocks", name: "BCE" },
  "BMO.TO": {
    ticker: "BMO.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Bank of Montreal",
  },
  "BNS.TO": {
    ticker: "BNS.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Bank of Nova Scotia",
  },
  "CAR-UN.TO": {
    ticker: "CAR-UN.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Canadian Apartment Properties REIT",
  },
  "CCO.TO": { ticker: "CCO.TO", currency: "CAD", category: "Canadian stocks", name: "Cameco" },
  "CGI.TO": { ticker: "CGI.TO", currency: "CAD", category: "Canadian stocks", name: "Celestica" },
  "CHP-UN.TO": {
    ticker: "CHP-UN.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Choice Properties REIT",
  },
  "CM.TO": {
    ticker: "CM.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Canadian Imperial Bank of Commerce",
  },
  "CNQ.TO": {
    ticker: "CNQ.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Canadian Natural Resources",
  },
  "CNR.TO": {
    ticker: "CNR.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Canadian National Railway",
  },
  "CP.TO": {
    ticker: "CP.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Canadian Pacific Kansas City",
  },
  "CSU.TO": {
    ticker: "CSU.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Constellation Software",
  },
  "CTC-A.TO": {
    ticker: "CTC-A.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Canadian Tire",
  },
  "CVE.TO": {
    ticker: "CVE.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Cenovus Energy",
  },
  "DOL.TO": { ticker: "DOL.TO", currency: "CAD", category: "Canadian stocks", name: "Dollarama" },
  "EMA.TO": { ticker: "EMA.TO", currency: "CAD", category: "Canadian stocks", name: "Emera" },
  "EMP-A.TO": {
    ticker: "EMP-A.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Empire Company",
  },
  "ENB.TO": {
    ticker: "ENB.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Enbridge",
    note: "Pipeline operator. Dividend-yield SM target.",
  },
  "FFH.TO": {
    ticker: "FFH.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Fairfax Financial Holdings",
  },
  "FM.TO": {
    ticker: "FM.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "First Quantum Minerals",
  },
  "FNV.TO": {
    ticker: "FNV.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Franco-Nevada",
  },
  "FTS.TO": { ticker: "FTS.TO", currency: "CAD", category: "Canadian stocks", name: "Fortis" },
  "GEI.TO": {
    ticker: "GEI.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Gibson Energy",
  },
  "GFL.TO": {
    ticker: "GFL.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "GFL Environmental",
  },
  "GIB-A.TO": {
    ticker: "GIB-A.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "CGI Group",
  },
  "GIL.TO": {
    ticker: "GIL.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Gildan Activewear",
  },
  "GOLD.TO": {
    ticker: "GOLD.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Barrick Gold",
  },
  "GWO.TO": {
    ticker: "GWO.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Great-West Lifeco",
  },
  "H.TO": { ticker: "H.TO", currency: "CAD", category: "Canadian stocks", name: "Hydro One" },
  "HR-UN.TO": {
    ticker: "HR-UN.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "H&R REIT",
  },
  "IFC.TO": {
    ticker: "IFC.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Intact Financial",
  },
  "IMO.TO": {
    ticker: "IMO.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Imperial Oil",
  },
  "K.TO": { ticker: "K.TO", currency: "CAD", category: "Canadian stocks", name: "Kinross Gold" },
  "KEY.TO": { ticker: "KEY.TO", currency: "CAD", category: "Canadian stocks", name: "Keyera" },
  "L.TO": { ticker: "L.TO", currency: "CAD", category: "Canadian stocks", name: "Loblaw" },
  "LSPD.TO": {
    ticker: "LSPD.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Lightspeed Commerce",
  },
  "MFC.TO": {
    ticker: "MFC.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Manulife Financial",
  },
  "MFI.TO": {
    ticker: "MFI.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Maple Leaf Foods",
  },
  "MG.TO": {
    ticker: "MG.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Magna International",
  },
  "MRU.TO": { ticker: "MRU.TO", currency: "CAD", category: "Canadian stocks", name: "Metro" },
  "NA.TO": {
    ticker: "NA.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "National Bank of Canada",
  },
  "NTR.TO": { ticker: "NTR.TO", currency: "CAD", category: "Canadian stocks", name: "Nutrien" },
  "OTEX.TO": { ticker: "OTEX.TO", currency: "CAD", category: "Canadian stocks", name: "OpenText" },
  "POW.TO": {
    ticker: "POW.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Power Corporation of Canada",
  },
  "PPL.TO": {
    ticker: "PPL.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Pembina Pipeline",
  },
  "QSR.TO": {
    ticker: "QSR.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Restaurant Brands International",
  },
  "RCI-B.TO": {
    ticker: "RCI-B.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Rogers Communications (Class B)",
  },
  "REI-UN.TO": {
    ticker: "REI-UN.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "RioCan REIT",
  },
  "RY.TO": {
    ticker: "RY.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Royal Bank of Canada",
    note: "Largest Canadian bank. Common single-name SM target.",
  },
  "SAP.TO": { ticker: "SAP.TO", currency: "CAD", category: "Canadian stocks", name: "Saputo" },
  "SHOP.TO": { ticker: "SHOP.TO", currency: "CAD", category: "Canadian stocks", name: "Shopify" },
  "SLF.TO": {
    ticker: "SLF.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Sun Life Financial",
  },
  "SU.TO": { ticker: "SU.TO", currency: "CAD", category: "Canadian stocks", name: "Suncor Energy" },
  "T.TO": { ticker: "T.TO", currency: "CAD", category: "Canadian stocks", name: "Telus" },
  "TD.TO": {
    ticker: "TD.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Toronto-Dominion Bank",
  },
  "TECK-B.TO": {
    ticker: "TECK-B.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Teck Resources (Class B)",
  },
  "TFII.TO": {
    ticker: "TFII.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "TFI International",
  },
  "TOU.TO": {
    ticker: "TOU.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Tourmaline Oil",
  },
  "TRI.TO": {
    ticker: "TRI.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Thomson Reuters",
  },
  "TRP.TO": { ticker: "TRP.TO", currency: "CAD", category: "Canadian stocks", name: "TC Energy" },
  "WCN.TO": {
    ticker: "WCN.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Waste Connections",
  },
  "WPM.TO": {
    ticker: "WPM.TO",
    currency: "CAD",
    category: "Canadian stocks",
    name: "Wheaton Precious Metals",
  },

  // ── US stocks (alphabetical by ticker) ──

  AAPL: { ticker: "AAPL", currency: "USD", category: "US stocks", name: "Apple" },
  ABBV: { ticker: "ABBV", currency: "USD", category: "US stocks", name: "AbbVie" },
  ABT: { ticker: "ABT", currency: "USD", category: "US stocks", name: "Abbott Laboratories" },
  ACN: { ticker: "ACN", currency: "USD", category: "US stocks", name: "Accenture" },
  ADBE: { ticker: "ADBE", currency: "USD", category: "US stocks", name: "Adobe" },
  ADI: { ticker: "ADI", currency: "USD", category: "US stocks", name: "Analog Devices" },
  AIG: {
    ticker: "AIG",
    currency: "USD",
    category: "US stocks",
    name: "American International Group",
  },
  AMAT: { ticker: "AMAT", currency: "USD", category: "US stocks", name: "Applied Materials" },
  AMD: { ticker: "AMD", currency: "USD", category: "US stocks", name: "Advanced Micro Devices" },
  AMGN: { ticker: "AMGN", currency: "USD", category: "US stocks", name: "Amgen" },
  AMT: { ticker: "AMT", currency: "USD", category: "US stocks", name: "American Tower" },
  AMZN: { ticker: "AMZN", currency: "USD", category: "US stocks", name: "Amazon" },
  ANET: { ticker: "ANET", currency: "USD", category: "US stocks", name: "Arista Networks" },
  AON: { ticker: "AON", currency: "USD", category: "US stocks", name: "Aon" },
  AVB: { ticker: "AVB", currency: "USD", category: "US stocks", name: "AvalonBay Communities" },
  AVGO: { ticker: "AVGO", currency: "USD", category: "US stocks", name: "Broadcom" },
  AXP: { ticker: "AXP", currency: "USD", category: "US stocks", name: "American Express" },
  BA: { ticker: "BA", currency: "USD", category: "US stocks", name: "Boeing" },
  BAC: { ticker: "BAC", currency: "USD", category: "US stocks", name: "Bank of America" },
  BKNG: { ticker: "BKNG", currency: "USD", category: "US stocks", name: "Booking Holdings" },
  BLK: { ticker: "BLK", currency: "USD", category: "US stocks", name: "BlackRock" },
  BMY: { ticker: "BMY", currency: "USD", category: "US stocks", name: "Bristol Myers Squibb" },
  "BRK-B": {
    ticker: "BRK-B",
    currency: "USD",
    category: "US stocks",
    name: "Berkshire Hathaway (Class B)",
  },
  BSX: { ticker: "BSX", currency: "USD", category: "US stocks", name: "Boston Scientific" },
  C: { ticker: "C", currency: "USD", category: "US stocks", name: "Citigroup" },
  CARR: { ticker: "CARR", currency: "USD", category: "US stocks", name: "Carrier Global" },
  CAT: { ticker: "CAT", currency: "USD", category: "US stocks", name: "Caterpillar" },
  CB: { ticker: "CB", currency: "USD", category: "US stocks", name: "Chubb" },
  CCL: { ticker: "CCL", currency: "USD", category: "US stocks", name: "Carnival" },
  CDNS: { ticker: "CDNS", currency: "USD", category: "US stocks", name: "Cadence Design Systems" },
  CI: { ticker: "CI", currency: "USD", category: "US stocks", name: "Cigna" },
  CL: { ticker: "CL", currency: "USD", category: "US stocks", name: "Colgate-Palmolive" },
  CMCSA: { ticker: "CMCSA", currency: "USD", category: "US stocks", name: "Comcast" },
  CME: { ticker: "CME", currency: "USD", category: "US stocks", name: "CME Group" },
  CMG: { ticker: "CMG", currency: "USD", category: "US stocks", name: "Chipotle Mexican Grill" },
  CNC: { ticker: "CNC", currency: "USD", category: "US stocks", name: "Centene" },
  COF: { ticker: "COF", currency: "USD", category: "US stocks", name: "Capital One Financial" },
  COP: { ticker: "COP", currency: "USD", category: "US stocks", name: "ConocoPhillips" },
  COST: { ticker: "COST", currency: "USD", category: "US stocks", name: "Costco" },
  CRM: { ticker: "CRM", currency: "USD", category: "US stocks", name: "Salesforce" },
  CRWD: { ticker: "CRWD", currency: "USD", category: "US stocks", name: "CrowdStrike" },
  CSCO: { ticker: "CSCO", currency: "USD", category: "US stocks", name: "Cisco Systems" },
  CSX: { ticker: "CSX", currency: "USD", category: "US stocks", name: "CSX" },
  CVS: { ticker: "CVS", currency: "USD", category: "US stocks", name: "CVS Health" },
  CVX: { ticker: "CVX", currency: "USD", category: "US stocks", name: "Chevron" },
  DAL: { ticker: "DAL", currency: "USD", category: "US stocks", name: "Delta Air Lines" },
  DE: { ticker: "DE", currency: "USD", category: "US stocks", name: "Deere" },
  DG: { ticker: "DG", currency: "USD", category: "US stocks", name: "Dollar General" },
  DHR: { ticker: "DHR", currency: "USD", category: "US stocks", name: "Danaher" },
  DIS: { ticker: "DIS", currency: "USD", category: "US stocks", name: "Walt Disney" },
  DLTR: { ticker: "DLTR", currency: "USD", category: "US stocks", name: "Dollar Tree" },
  DUK: { ticker: "DUK", currency: "USD", category: "US stocks", name: "Duke Energy" },
  DVN: { ticker: "DVN", currency: "USD", category: "US stocks", name: "Devon Energy" },
  EBAY: { ticker: "EBAY", currency: "USD", category: "US stocks", name: "eBay" },
  ECL: { ticker: "ECL", currency: "USD", category: "US stocks", name: "Ecolab" },
  ELV: { ticker: "ELV", currency: "USD", category: "US stocks", name: "Elevance Health" },
  EMR: { ticker: "EMR", currency: "USD", category: "US stocks", name: "Emerson Electric" },
  EOG: { ticker: "EOG", currency: "USD", category: "US stocks", name: "EOG Resources" },
  EQIX: { ticker: "EQIX", currency: "USD", category: "US stocks", name: "Equinix" },
  ETN: { ticker: "ETN", currency: "USD", category: "US stocks", name: "Eaton" },
  ETR: { ticker: "ETR", currency: "USD", category: "US stocks", name: "Entergy" },
  EXC: { ticker: "EXC", currency: "USD", category: "US stocks", name: "Exelon" },
  F: { ticker: "F", currency: "USD", category: "US stocks", name: "Ford Motor" },
  FANG: { ticker: "FANG", currency: "USD", category: "US stocks", name: "Diamondback Energy" },
  FCX: { ticker: "FCX", currency: "USD", category: "US stocks", name: "Freeport-McMoRan" },
  FDX: { ticker: "FDX", currency: "USD", category: "US stocks", name: "FedEx" },
  FTNT: { ticker: "FTNT", currency: "USD", category: "US stocks", name: "Fortinet" },
  GD: { ticker: "GD", currency: "USD", category: "US stocks", name: "General Dynamics" },
  GE: { ticker: "GE", currency: "USD", category: "US stocks", name: "GE Aerospace" },
  GILD: { ticker: "GILD", currency: "USD", category: "US stocks", name: "Gilead Sciences" },
  GM: { ticker: "GM", currency: "USD", category: "US stocks", name: "General Motors" },
  GOOG: { ticker: "GOOG", currency: "USD", category: "US stocks", name: "Alphabet (Class C)" },
  GOOGL: { ticker: "GOOGL", currency: "USD", category: "US stocks", name: "Alphabet (Class A)" },
  GS: { ticker: "GS", currency: "USD", category: "US stocks", name: "Goldman Sachs" },
  HCA: { ticker: "HCA", currency: "USD", category: "US stocks", name: "HCA Healthcare" },
  HD: { ticker: "HD", currency: "USD", category: "US stocks", name: "Home Depot" },
  HLT: { ticker: "HLT", currency: "USD", category: "US stocks", name: "Hilton Worldwide" },
  HON: { ticker: "HON", currency: "USD", category: "US stocks", name: "Honeywell" },
  IBM: { ticker: "IBM", currency: "USD", category: "US stocks", name: "IBM" },
  ICE: { ticker: "ICE", currency: "USD", category: "US stocks", name: "Intercontinental Exchange" },
  ILMN: { ticker: "ILMN", currency: "USD", category: "US stocks", name: "Illumina" },
  INTC: { ticker: "INTC", currency: "USD", category: "US stocks", name: "Intel" },
  INTU: { ticker: "INTU", currency: "USD", category: "US stocks", name: "Intuit" },
  ISRG: { ticker: "ISRG", currency: "USD", category: "US stocks", name: "Intuitive Surgical" },
  ITW: { ticker: "ITW", currency: "USD", category: "US stocks", name: "Illinois Tool Works" },
  JNJ: { ticker: "JNJ", currency: "USD", category: "US stocks", name: "Johnson & Johnson" },
  JPM: { ticker: "JPM", currency: "USD", category: "US stocks", name: "JPMorgan Chase" },
  KLAC: { ticker: "KLAC", currency: "USD", category: "US stocks", name: "KLA Corporation" },
  KMB: { ticker: "KMB", currency: "USD", category: "US stocks", name: "Kimberly-Clark" },
  KMI: { ticker: "KMI", currency: "USD", category: "US stocks", name: "Kinder Morgan" },
  KO: { ticker: "KO", currency: "USD", category: "US stocks", name: "Coca-Cola" },
  LIN: { ticker: "LIN", currency: "USD", category: "US stocks", name: "Linde" },
  LLY: { ticker: "LLY", currency: "USD", category: "US stocks", name: "Eli Lilly" },
  LMT: { ticker: "LMT", currency: "USD", category: "US stocks", name: "Lockheed Martin" },
  LOW: { ticker: "LOW", currency: "USD", category: "US stocks", name: "Lowe's" },
  LRCX: { ticker: "LRCX", currency: "USD", category: "US stocks", name: "Lam Research" },
  MA: { ticker: "MA", currency: "USD", category: "US stocks", name: "Mastercard" },
  MAR: { ticker: "MAR", currency: "USD", category: "US stocks", name: "Marriott International" },
  MCD: { ticker: "MCD", currency: "USD", category: "US stocks", name: "McDonald's" },
  MCO: { ticker: "MCO", currency: "USD", category: "US stocks", name: "Moody's" },
  MDLZ: { ticker: "MDLZ", currency: "USD", category: "US stocks", name: "Mondelez International" },
  MDT: { ticker: "MDT", currency: "USD", category: "US stocks", name: "Medtronic" },
  MET: { ticker: "MET", currency: "USD", category: "US stocks", name: "MetLife" },
  META: { ticker: "META", currency: "USD", category: "US stocks", name: "Meta Platforms" },
  MMM: { ticker: "MMM", currency: "USD", category: "US stocks", name: "3M" },
  MO: { ticker: "MO", currency: "USD", category: "US stocks", name: "Altria" },
  MPC: { ticker: "MPC", currency: "USD", category: "US stocks", name: "Marathon Petroleum" },
  MRK: { ticker: "MRK", currency: "USD", category: "US stocks", name: "Merck" },
  MRNA: { ticker: "MRNA", currency: "USD", category: "US stocks", name: "Moderna" },
  MRVL: { ticker: "MRVL", currency: "USD", category: "US stocks", name: "Marvell Technology" },
  MS: { ticker: "MS", currency: "USD", category: "US stocks", name: "Morgan Stanley" },
  MSFT: { ticker: "MSFT", currency: "USD", category: "US stocks", name: "Microsoft" },
  MU: { ticker: "MU", currency: "USD", category: "US stocks", name: "Micron Technology" },
  NEE: { ticker: "NEE", currency: "USD", category: "US stocks", name: "NextEra Energy" },
  NEM: { ticker: "NEM", currency: "USD", category: "US stocks", name: "Newmont" },
  NFLX: { ticker: "NFLX", currency: "USD", category: "US stocks", name: "Netflix" },
  NKE: { ticker: "NKE", currency: "USD", category: "US stocks", name: "Nike" },
  NOC: { ticker: "NOC", currency: "USD", category: "US stocks", name: "Northrop Grumman" },
  NOW: { ticker: "NOW", currency: "USD", category: "US stocks", name: "ServiceNow" },
  NSC: { ticker: "NSC", currency: "USD", category: "US stocks", name: "Norfolk Southern" },
  NUE: { ticker: "NUE", currency: "USD", category: "US stocks", name: "Nucor" },
  NVDA: { ticker: "NVDA", currency: "USD", category: "US stocks", name: "NVIDIA" },
  NXPI: { ticker: "NXPI", currency: "USD", category: "US stocks", name: "NXP Semiconductors" },
  O: { ticker: "O", currency: "USD", category: "US stocks", name: "Realty Income" },
  ORCL: { ticker: "ORCL", currency: "USD", category: "US stocks", name: "Oracle" },
  OXY: { ticker: "OXY", currency: "USD", category: "US stocks", name: "Occidental Petroleum" },
  PANW: { ticker: "PANW", currency: "USD", category: "US stocks", name: "Palo Alto Networks" },
  PEP: { ticker: "PEP", currency: "USD", category: "US stocks", name: "PepsiCo" },
  PFE: { ticker: "PFE", currency: "USD", category: "US stocks", name: "Pfizer" },
  PG: { ticker: "PG", currency: "USD", category: "US stocks", name: "Procter & Gamble" },
  PLD: { ticker: "PLD", currency: "USD", category: "US stocks", name: "Prologis" },
  PM: { ticker: "PM", currency: "USD", category: "US stocks", name: "Philip Morris International" },
  PRU: { ticker: "PRU", currency: "USD", category: "US stocks", name: "Prudential Financial" },
  PSX: { ticker: "PSX", currency: "USD", category: "US stocks", name: "Phillips 66" },
  PYPL: { ticker: "PYPL", currency: "USD", category: "US stocks", name: "PayPal" },
  QCOM: { ticker: "QCOM", currency: "USD", category: "US stocks", name: "Qualcomm" },
  RCL: { ticker: "RCL", currency: "USD", category: "US stocks", name: "Royal Caribbean" },
  REGN: {
    ticker: "REGN",
    currency: "USD",
    category: "US stocks",
    name: "Regeneron Pharmaceuticals",
  },
  RTX: { ticker: "RTX", currency: "USD", category: "US stocks", name: "RTX Corporation" },
  SBUX: { ticker: "SBUX", currency: "USD", category: "US stocks", name: "Starbucks" },
  SCHW: { ticker: "SCHW", currency: "USD", category: "US stocks", name: "Charles Schwab" },
  SHW: { ticker: "SHW", currency: "USD", category: "US stocks", name: "Sherwin-Williams" },
  SLB: { ticker: "SLB", currency: "USD", category: "US stocks", name: "Schlumberger" },
  SNPS: { ticker: "SNPS", currency: "USD", category: "US stocks", name: "Synopsys" },
  SO: { ticker: "SO", currency: "USD", category: "US stocks", name: "Southern Company" },
  SPGI: { ticker: "SPGI", currency: "USD", category: "US stocks", name: "S&P Global" },
  STZ: { ticker: "STZ", currency: "USD", category: "US stocks", name: "Constellation Brands" },
  SYK: { ticker: "SYK", currency: "USD", category: "US stocks", name: "Stryker" },
  T: { ticker: "T", currency: "USD", category: "US stocks", name: "AT&T" },
  TGT: { ticker: "TGT", currency: "USD", category: "US stocks", name: "Target" },
  TJX: { ticker: "TJX", currency: "USD", category: "US stocks", name: "TJX Companies" },
  TMO: { ticker: "TMO", currency: "USD", category: "US stocks", name: "Thermo Fisher Scientific" },
  TMUS: { ticker: "TMUS", currency: "USD", category: "US stocks", name: "T-Mobile US" },
  TRV: { ticker: "TRV", currency: "USD", category: "US stocks", name: "The Travelers Companies" },
  TSLA: { ticker: "TSLA", currency: "USD", category: "US stocks", name: "Tesla" },
  TXN: { ticker: "TXN", currency: "USD", category: "US stocks", name: "Texas Instruments" },
  UBER: { ticker: "UBER", currency: "USD", category: "US stocks", name: "Uber Technologies" },
  UNH: { ticker: "UNH", currency: "USD", category: "US stocks", name: "UnitedHealth Group" },
  UNP: { ticker: "UNP", currency: "USD", category: "US stocks", name: "Union Pacific" },
  UPS: { ticker: "UPS", currency: "USD", category: "US stocks", name: "United Parcel Service" },
  USB: { ticker: "USB", currency: "USD", category: "US stocks", name: "U.S. Bancorp" },
  V: { ticker: "V", currency: "USD", category: "US stocks", name: "Visa" },
  VRTX: { ticker: "VRTX", currency: "USD", category: "US stocks", name: "Vertex Pharmaceuticals" },
  VZ: { ticker: "VZ", currency: "USD", category: "US stocks", name: "Verizon Communications" },
  WBD: { ticker: "WBD", currency: "USD", category: "US stocks", name: "Warner Bros. Discovery" },
  WFC: { ticker: "WFC", currency: "USD", category: "US stocks", name: "Wells Fargo" },
  WM: { ticker: "WM", currency: "USD", category: "US stocks", name: "Waste Management" },
  WMB: { ticker: "WMB", currency: "USD", category: "US stocks", name: "Williams Companies" },
  WMT: { ticker: "WMT", currency: "USD", category: "US stocks", name: "Walmart" },
  XOM: { ticker: "XOM", currency: "USD", category: "US stocks", name: "Exxon Mobil" },
  YUM: { ticker: "YUM", currency: "USD", category: "US stocks", name: "Yum! Brands" },
  ZS: { ticker: "ZS", currency: "USD", category: "US stocks", name: "Zscaler" },
  ZTS: { ticker: "ZTS", currency: "USD", category: "US stocks", name: "Zoetis" },
} as const;

/** Convenience helper — list of all canonical symbols. */
export const TICKER_SYMBOLS = Object.keys(TICKER_CONFIG) as readonly string[];
